import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Lock,
  CheckCircle2,
  BadgeCheck,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, api, goToLogin } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MarkSolvedModal from "@/components/MarkSolvedModal";
import { OPEN_PROBLEMS } from "@/data/openProblems";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BACKEND}/api` });

const URGENCY_STYLES = {
  Today: "bg-[#FEE2E2] text-[#B91C1C]",
  "This week": "bg-[#FEF3C7] text-[#B45309]",
  "This month": "bg-[#DBEAFE] text-[#1E40AF]",
  "No rush": "bg-[#F3F4F6] text-[#4B5563]",
};

export default function ProblemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [problem, setProblem] = useState(null);
  const [subs, setSubs] = useState([]);
  const [gated, setGated] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);

  const fetchAll = async () => {
    // Demo problems live only in frontend mock data.
    const demo = OPEN_PROBLEMS.find((p) => p.id === id);
    if (demo) {
      setProblem({
        problem_id: demo.id,
        posted_by_user_id: demo.postedBy.personId || "",
        posted_by_name: demo.postedBy.name,
        posted_by_picture: demo.postedBy.photo,
        posted_by_role: demo.postedBy.role,
        title: demo.title,
        category: demo.category,
        description: demo.description,
        desired_outcome: "",
        urgency: demo.urgency,
        interested_count: demo.interestedCount,
        status: "open",
        solution_artefact: "",
        solution_tools: [],
        solution_models: [],
        created_at: demo.datePosted,
        isDemo: true,
      });
      setSubs([]);
      return;
    }
    try {
      const client = user ? api : publicAxios;
      const { data } = await client.get(`/open-problems/${id}`);
      setProblem(data);
      if (data.status === "closed" && !user) {
        setGated(true);
      } else {
        setGated(false);
        try {
          const s = await client.get(`/open-problems/${id}/submissions`);
          setSubs(s.data || []);
        } catch {
          setSubs([]);
        }
      }
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
    }
  };

  useEffect(() => {
    if (!loading) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, loading]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Problem not found</h1>
        <button
          onClick={() => navigate("/problems")}
          className="mt-6 px-5 py-2.5 rounded-full bg-[#3FFFB6] text-[#111827] text-sm font-semibold"
        >
          Back to problems
        </button>
      </div>
    );
  }
  if (!problem)
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#9CA3AF]">
        Loading…
      </div>
    );

  const isPoster = user && user.user_id === problem.posted_by_user_id;
  const isClosed = problem.status === "closed";
  const isDemo = problem.isDemo === true;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return goToLogin(`/problems/${id}`);
    if (!reply.trim()) return toast.error("Write a solution first.");
    setPosting(true);
    try {
      const { data } = await api.post(`/open-problems/${id}/submissions`, {
        body: reply.trim(),
      });
      setSubs([...subs, data]);
      setReply("");
      toast.success("Solution submitted.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit.";
      toast.error(msg);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link
        to={isClosed ? "/closed-problems" : "/problems"}
        className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#111827]"
      >
        <ArrowLeft size={16} />
        Back
      </Link>

      <article className="mt-6 bg-white rounded-3xl ring-1 ring-[#F3F4F6] p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/categories/${encodeURIComponent(problem.category)}`}
              data-testid="problem-category-link"
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563] hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
            >
              {problem.category}
            </Link>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${URGENCY_STYLES[problem.urgency] || ""}`}
            >
              {problem.urgency}
            </span>
            {isClosed && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-[#065F46] bg-[#ECFDF5] ring-1 ring-[#D1FAE5]">
                <CheckCircle2 size={12} />
                Solved
              </span>
            )}
          </div>
          {isPoster && !isClosed && (
            <Button
              onClick={() => setMarkOpen(true)}
              data-testid="mark-solved"
              className="rounded-full bg-[#111827] hover:bg-black text-white font-semibold px-5 h-10"
            >
              <BadgeCheck size={16} className="mr-2" />
              Mark as solved
            </Button>
          )}
        </div>

        <h1
          data-testid="problem-title"
          className="mt-5 font-display font-bold text-3xl leading-tight text-[#111827]"
        >
          {problem.title}
        </h1>

        <div className="mt-4 flex items-center gap-3 text-sm text-[#4B5563]">
          <img
            src={problem.posted_by_picture || ""}
            alt=""
            onError={(e) => (e.currentTarget.style.display = "none")}
            className="w-8 h-8 rounded-full object-cover bg-[#F3F4F6]"
          />
          <span>Posted by {problem.posted_by_name}</span>
          <span className="text-[#9CA3AF]">·</span>
          <span className="inline-flex items-center gap-1 text-[#9CA3AF]">
            <Calendar size={12} /> {problem.created_at?.slice(0, 10)}
          </span>
        </div>

        <p className="mt-6 text-[#111827] leading-relaxed whitespace-pre-line">
          {problem.description}
        </p>

        {problem.desired_outcome && (
          <div className="mt-5 rounded-xl bg-[#FAFAFA] ring-1 ring-[#F3F4F6] px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1">
              What solved looks like
            </p>
            <p className="text-sm text-[#111827]">{problem.desired_outcome}</p>
          </div>
        )}
      </article>

      {/* Closed artefact */}
      {isClosed && !gated && (
        <section className="mt-8 bg-white rounded-3xl ring-1 ring-[#D1FAE5] p-8">
          <div className="flex items-center gap-2 text-[#065F46] mb-3">
            <CheckCircle2 size={18} />
            <p className="font-display font-bold text-lg">
              Solved by{" "}
              {problem.solved_by_user_id ? (
                <Link
                  to={`/people/${problem.solved_by_user_id}`}
                  className="underline decoration-2 underline-offset-2"
                >
                  {problem.solved_by_name}
                </Link>
              ) : (
                problem.solved_by_name
              )}
              {problem.solved_off_platform && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] bg-[#F3F4F6] rounded-full px-2 py-0.5">
                  Off-platform
                </span>
              )}
            </p>
          </div>
          <p className="text-[#111827] leading-relaxed whitespace-pre-line">
            {problem.solution_artefact}
          </p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {(problem.solution_models || []).map((m) => (
              <Link
                key={m}
                to={`/tools/${encodeURIComponent(m)}`}
                data-testid={`solved-model-${m}`}
                className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
              >
                {m}
              </Link>
            ))}
            {(problem.solution_tools || []).map((t) => (
              <Link
                key={t}
                to={`/tools/${encodeURIComponent(t)}`}
                data-testid={`solved-tool-${t}`}
                className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563] hover:bg-[#D6FFEB] hover:text-[#3FFFB6] transition-colors"
              >
                {t}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Gated closed artefact for unauth */}
      {gated && (
        <section className="mt-8 bg-white rounded-3xl ring-1 ring-[#F3F4F6] p-10 flex flex-col items-center text-center">
          <span className="w-14 h-14 rounded-full bg-[#D6FFEB] text-[#3FFFB6] flex items-center justify-center">
            <Lock size={22} />
          </span>
          <h2 className="mt-5 font-display font-bold text-2xl text-[#111827]">
            Solutions are for members
          </h2>
          <p className="mt-2 max-w-md text-[#4B5563]">
            Sign in with Google to read the full solution artefact, tools, and
            models used.
          </p>
          <Button
            onClick={() => goToLogin(`/problems/${id}`)}
            data-testid="gate-signin"
            className="mt-5 h-11 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold px-6"
          >
            Continue with Google
          </Button>
        </section>
      )}

      {/* Demo banner */}
      {isDemo && (
        <section
          data-testid="demo-banner"
          className="mt-8 bg-[#EBFFF5] rounded-3xl ring-1 ring-[#B8FFDD] p-6"
        >
          <p className="font-display font-semibold text-[#B03A3A]">
            This is a demo problem
          </p>
          <p className="mt-1 text-sm text-[#4B5563]">
            It's here to show what a real problem looks like. To submit solutions,
            mark solved, or express interest, browse real open problems or post
            your own.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/problems"
              className="inline-flex items-center px-4 h-9 rounded-full bg-white ring-1 ring-[#E5E7EB] text-[#111827] text-sm font-semibold hover:ring-[#111827]"
            >
              Browse real problems
            </Link>
            <Link
              to="/problems"
              onClick={(e) => {
                e.preventDefault();
                // Trigger the modal on OpenProblemsPage via URL nav; the CTA there is enough for MVP.
                window.location.href = "/problems";
              }}
              className="inline-flex items-center px-4 h-9 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold"
            >
              Post your own problem
            </Link>
          </div>
        </section>
      )}

      {/* Submissions */}
      {!isClosed && !isDemo && (
        <section className="mt-8">
          <h2 className="font-display font-bold text-xl text-[#111827]">
            Solutions ({subs.length})
          </h2>

          <ul className="mt-4 space-y-3">
            {subs.map((s) => (
              <li
                key={s.submission_id}
                data-testid={`submission-${s.submission_id}`}
                className="bg-white rounded-2xl ring-1 ring-[#F3F4F6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <img
                    src={s.user_picture || ""}
                    alt=""
                    onError={(e) => (e.currentTarget.style.display = "none")}
                    className="w-8 h-8 rounded-full object-cover bg-[#F3F4F6]"
                  />
                  <div>
                    <Link
                      to={`/people/${s.user_id}`}
                      className="text-sm font-medium text-[#111827] hover:text-[#3FFFB6]"
                    >
                      {s.user_name}
                    </Link>
                    <p className="text-xs text-[#9CA3AF]">{s.user_role}</p>
                  </div>
                </div>
                <p className="mt-3 text-[#111827] leading-relaxed whitespace-pre-line">
                  {s.body}
                </p>
              </li>
            ))}
          </ul>

          {user && !isPoster && !subs.find((s) => s.user_id === user?.user_id) && (
            <form
              onSubmit={submit}
              className="mt-6 bg-white rounded-2xl ring-1 ring-[#F3F4F6] p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">
                Submit your solution
              </p>
              <Textarea
                data-testid="submission-body"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Walk them through how you'd solve this…"
                className="min-h-28 rounded-xl"
              />
              <Button
                type="submit"
                data-testid="submission-submit"
                disabled={posting}
                className="mt-4 h-11 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold px-6"
              >
                <Send size={16} className="mr-2" />
                {posting ? "Submitting…" : "Submit solution"}
              </Button>
            </form>
          )}

          {!user && !loading && (
            <div className="mt-6 bg-white rounded-2xl ring-1 ring-[#F3F4F6] p-5">
              <p className="text-sm text-[#4B5563]">
                Sign in to submit a solution.
              </p>
              <Button
                onClick={() => goToLogin(`/problems/${id}`)}
                className="mt-3 h-10 rounded-full bg-[#111827] hover:bg-black text-white text-sm font-semibold px-5"
              >
                Continue with Google
              </Button>
            </div>
          )}
        </section>
      )}

      {isPoster && !isDemo && (
        <MarkSolvedModal
          open={markOpen}
          onOpenChange={setMarkOpen}
          problem={problem}
          submissions={subs}
          onSolved={(updated) => {
            setProblem(updated);
            toast.success("Marked as solved.");
          }}
        />
      )}
    </div>
  );
}
